import React, { useEffect, useState } from "react";
import { Beaker, Sparkles, ShieldAlert, Trash2, ArrowRight, Loader2, Plus, X, ListMusic, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import ShareModal from "@/components/ShareModal";

const STYLES = [
  { key: "scientific", label: "Scientific" },
  { key: "fun-kids",   label: "Fun (Kids)" },
  { key: "dramatic",   label: "Dramatic" },
];

const SAMPLE_IDEAS = [
  ["Baking soda", "Vinegar"],
  ["Salt", "Ice", "Water"],
  ["Copper wire", "Lemon", "Zinc nail"],
  ["Sugar", "Yeast", "Warm water"],
];

export default function Lab() {
  const { user } = useAuth();
  const [ingredients, setIngredients] = useState([]);
  const [input, setInput] = useState("");
  const [goal, setGoal] = useState("");
  const [style, setStyle] = useState("scientific");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedForPlaylist, setSelectedForPlaylist] = useState([]);
  const [playlistTitle, setPlaylistTitle] = useState("");
  const [shareOpen, setShareOpen] = useState(false);

  useSeo({
    title: "AI Practical Lab · find2near",
    description: "Type ingredients — AI narrates the reaction step by step and paints the illustration.",
    canonical: "/lab",
  });

  useEffect(() => {
    if (!user) return;
    api.get("/lab/history").then((r) => setHistory(r.data)).catch(() => {});
  }, [user]);

  const addIngredient = () => {
    const v = input.trim();
    if (!v) return;
    if (ingredients.length >= 12) {
      toast.error("Max 12 ingredients per experiment.");
      return;
    }
    setIngredients([...ingredients, v]);
    setInput("");
  };

  const removeIngredient = (idx) => {
    setIngredients(ingredients.filter((_, i) => i !== idx));
  };

  const applySample = (arr) => setIngredients(arr);

  const togglePlaylistPick = (id) => {
    setSelectedForPlaylist((s) => s.includes(id) ? s.filter(x => x !== id) : (s.length >= 8 ? s : [...s, id]));
  };

  const savePlaylist = async () => {
    if (selectedForPlaylist.length < 1) { toast.error("Select at least 1 experiment"); return; }
    if (!playlistTitle.trim()) { toast.error("Give the playlist a title"); return; }
    try {
      const r = await api.post("/lab/playlists", {
        title: playlistTitle,
        experiment_ids: selectedForPlaylist,
        public: true,
      });
      toast.success(`Playlist saved · share /pl/${r.data.playlist_id}`);
      setSelectedForPlaylist([]);
      setPlaylistTitle("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const submit = async () => {
    if (ingredients.length === 0) {
      toast.error("Add at least one ingredient.");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/lab/experiment", { ingredients, goal, style });
      setResult(r.data);
      const h = await api.get("/lab/history");
      setHistory(h.data);
      toast.success("Reaction generated · ₹1 debited.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Experiment failed");
    } finally {
      setBusy(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-md mx-auto text-center py-24 px-6">
        <Beaker className="mx-auto text-fuchsia-500" size={48} />
        <h2 className="heading text-2xl font-extrabold mt-4">Sign in to run experiments</h2>
        <p className="text-stone-500 mt-2 text-sm">Each experiment costs ₹1 from your wallet (₹50 free credit on sign-up).</p>
        <Link to="/onboarding" className="inline-block mt-6 bg-fuchsia-600 text-white px-6 py-3 rounded-full font-bold" data-testid="lab-signin">Get started</Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-8" data-testid="lab-page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">AI Practical Lab</div>
          <h1 className="heading text-3xl md:text-4xl font-extrabold text-stone-900">
            Type it. <span className="text-fuchsia-600">See it react.</span>
          </h1>
          <p className="text-stone-500 text-sm mt-1 max-w-lg">
            Enter ingredients or elements — AI writes the step-by-step reaction, safety warnings, expected products, and paints an illustration.
          </p>
        </div>
        <div className="rounded-full bg-fuchsia-50 border border-fuchsia-200 px-3 py-1 text-xs font-black text-fuchsia-700 shrink-0">
          ₹1 per run
        </div>
      </div>

      {/* Input card */}
      <div className="rounded-3xl bg-white border border-stone-200 p-5 md:p-6 shadow-sm">
        <label className="text-xs uppercase tracking-widest font-black text-stone-500">Ingredients</label>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {ingredients.map((ing, i) => (
            <span key={i} className="inline-flex items-center gap-1 rounded-full bg-fuchsia-100 text-fuchsia-800 px-3 py-1.5 text-sm font-bold" data-testid={`lab-chip-${i}`}>
              {ing}
              <button onClick={() => removeIngredient(i)} className="ml-1 opacity-60 hover:opacity-100" aria-label="Remove">
                <X size={14} />
              </button>
            </span>
          ))}
          <div className="inline-flex items-center gap-1 border border-stone-200 rounded-full pl-3 pr-1 py-1">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addIngredient())}
              placeholder="e.g. Baking soda"
              className="text-sm bg-transparent outline-none w-40"
              data-testid="lab-input"
            />
            <button
              onClick={addIngredient}
              className="w-7 h-7 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white grid place-items-center"
              data-testid="lab-add"
            >
              <Plus size={14} />
            </button>
          </div>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <div>
            <label className="text-xs uppercase tracking-widest font-black text-stone-500">Optional goal</label>
            <input
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder="e.g. make soap, understand the reaction"
              className="mt-2 w-full px-4 py-2 rounded-full border border-stone-200 text-sm outline-none focus:border-fuchsia-500"
              data-testid="lab-goal"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-black text-stone-500">Tone</label>
            <div className="mt-2 flex gap-2">
              {STYLES.map((s) => (
                <button
                  key={s.key}
                  onClick={() => setStyle(s.key)}
                  className={`flex-1 py-2 rounded-full text-xs font-bold transition-colors ${
                    style === s.key
                      ? "bg-fuchsia-600 text-white"
                      : "bg-stone-100 text-stone-700 hover:bg-stone-200"
                  }`}
                  data-testid={`lab-style-${s.key}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-widest font-black text-stone-500 mb-2">Or try one of these</div>
          <div className="flex flex-wrap gap-2">
            {SAMPLE_IDEAS.map((arr, i) => (
              <button
                key={i}
                onClick={() => applySample(arr)}
                className="text-xs font-semibold px-3 py-1.5 rounded-full bg-white border border-stone-200 hover:border-fuchsia-500 hover:text-fuchsia-600 transition-colors"
                data-testid={`lab-sample-${i}`}
              >
                {arr.join(" + ")}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={submit}
          disabled={busy || ingredients.length === 0}
          className="mt-5 w-full md:w-auto bg-gradient-to-r from-fuchsia-600 to-purple-700 hover:from-fuchsia-700 hover:to-purple-800 text-white font-black px-6 py-3 rounded-full inline-flex items-center gap-2 transition-colors disabled:opacity-50"
          data-testid="lab-run"
        >
          {busy ? <><Loader2 size={18} className="animate-spin" /> Reacting…</> : <><Sparkles size={18} /> Run experiment · ₹1</>}
        </button>
      </div>

      {/* Result */}
      {result && (
        <div className="mt-6 rounded-3xl bg-gradient-to-br from-slate-50 to-fuchsia-50 border border-fuchsia-200 p-5 md:p-6" data-testid="lab-result">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h2 className="heading text-2xl font-black text-stone-900">{result.result?.title || "Reaction"}</h2>
              <ol className="mt-3 space-y-2 list-decimal list-inside text-sm text-stone-700 leading-relaxed">
                {(result.result?.steps || []).map((s, i) => (
                  <li key={i} data-testid={`lab-step-${i}`}>{s}</li>
                ))}
              </ol>
              {result.result?.fun_fact && (
                <div className="mt-4 rounded-2xl bg-white border border-stone-200 px-4 py-3 text-sm">
                  <span className="text-[10px] uppercase tracking-widest font-black text-fuchsia-600">Fun fact</span>
                  <div className="text-stone-700 mt-0.5">{result.result.fun_fact}</div>
                </div>
              )}
            </div>
            <div>
              {result.image_url && (
                <img
                  src={`${API}${result.image_url.replace(/^\/api/, '')}`}
                  alt="Reaction illustration"
                  className="w-full rounded-2xl border border-stone-200 shadow-sm"
                  data-testid="lab-image"
                />
              )}
              {(result.result?.safety?.length > 0) && (
                <div className="mt-4 rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3">
                  <div className="flex items-center gap-2 text-amber-800 text-xs font-black uppercase tracking-widest">
                    <ShieldAlert size={14} /> Safety
                  </div>
                  <ul className="mt-2 space-y-1 text-sm text-amber-900 list-disc list-inside">
                    {result.result.safety.map((s, i) => (<li key={i}>{s}</li>))}
                  </ul>
                </div>
              )}
              {(result.result?.products?.length > 0) && (
                <div className="mt-4 text-sm">
                  <span className="text-[10px] uppercase tracking-widest font-black text-emerald-700">Expected products</span>
                  <div className="text-stone-700 mt-0.5">{result.result.products.join(", ")}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="mt-10">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs uppercase tracking-widest font-black text-stone-500">Recent experiments</h3>
            {selectedForPlaylist.length > 0 && (
              <div className="flex items-center gap-2">
                <input
                  value={playlistTitle}
                  onChange={(e) => setPlaylistTitle(e.target.value)}
                  placeholder="Playlist title"
                  className="text-xs px-3 py-1.5 rounded-full border border-fuchsia-300 outline-none"
                  data-testid="lab-playlist-title"
                />
                <button
                  onClick={savePlaylist}
                  className="text-xs font-black px-3 py-1.5 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white inline-flex items-center gap-1"
                  data-testid="lab-playlist-save"
                >
                  <ListMusic size={13} /> Save playlist ({selectedForPlaylist.length})
                </button>
              </div>
            )}
          </div>
          <ul className="grid gap-2">
            {history.slice(0, 10).map((h) => {
              const picked = selectedForPlaylist.includes(h.experiment_id);
              return (
                <li key={h.experiment_id} className={`rounded-2xl bg-white border p-3 flex items-center gap-3 ${picked ? "border-fuchsia-500" : "border-stone-200"}`} data-testid={`lab-history-${h.experiment_id}`}>
                  <button onClick={() => togglePlaylistPick(h.experiment_id)} className={`w-6 h-6 rounded-md border-2 grid place-items-center ${picked ? "bg-fuchsia-600 border-fuchsia-600 text-white" : "border-stone-300 text-transparent"}`} title="Add to playlist" data-testid={`lab-pick-${h.experiment_id}`}>
                    ✓
                  </button>
                  <div className="w-8 h-8 rounded-lg bg-fuchsia-100 text-fuchsia-700 grid place-items-center shrink-0">
                    <Beaker size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-stone-900 truncate">{h.result?.title || h.ingredients.join(" + ")}</div>
                    <div className="text-[11px] text-stone-500 truncate">{h.ingredients.join(", ")} · {new Date(h.created_at).toLocaleString()}</div>
                  </div>
                  <button onClick={() => setResult(h)} className="text-fuchsia-600 hover:text-fuchsia-800 text-sm font-bold flex items-center gap-1">
                    Open <ArrowRight size={14} />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        kind="lab"
        id={result?.experiment_id}
        title={result?.result?.title || "AI Lab experiment"}
        url={result ? `${window.location.origin}/lab#${result.experiment_id}` : undefined}
      />
    </div>
  );
}